import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Container, Box, Button, IconButton, Menu, MenuItem, Paper, Divider, Collapse, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import BugReportIcon from '@mui/icons-material/BugReport';
import MonitorIcon from '@mui/icons-material/Monitor';
import HomeIcon from '@mui/icons-material/Home';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BusinessIcon from '@mui/icons-material/Business';
import BackupIcon from '@mui/icons-material/Backup';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import authService from '../services/auth';
import Logo from '../assets/logo.png';

const knowledgeBaseDocuments = [
  // adminOnly: the route behind this is admin-gated, so hide it from annotators
  // rather than offering a link that lands on a permission error.
  { text: '1st Research Paper', path: '/mvp-overview/research-paper', icon: <SchoolIcon fontSize="small" /> },
  { text: '2nd Research Paper', path: '/mvp-overview/research-paper-2', icon: <SchoolIcon fontSize="small" /> },
  { text: 'Paper 1 Annotation Stats', path: '/mvp-overview/paper1-annotation-stats', icon: <FactCheckIcon fontSize="small" />, adminOnly: true },
  { text: 'Verified / AI-Inferred Validation', path: '/mvp-overview/ai-inferred-stats', icon: <FactCheckIcon fontSize="small" />, adminOnly: true },
  { text: 'RAG NIST Mapping — Ground-Truth Annotations', path: '/mvp-overview/rag-ground-truth', icon: <FactCheckIcon fontSize="small" /> },
  { text: 'RAG NIST Mapping — Stratified Ground Truth (Paper 2)', path: '/mvp-overview/stratified-ground-truth', icon: <FactCheckIcon fontSize="small" /> },
  { text: 'Provisional Patent Draft', path: '/mvp-overview/provisional-patent', icon: <GavelIcon fontSize="small" /> },
  { text: 'Market Validation Report', path: '/mvp-overview/market-validation', icon: <AssessmentIcon fontSize="small" /> },
  { text: 'NSCAT Pitch', path: '/mvp-overview/nscat-pitch', icon: <RocketLaunchIcon fontSize="small" /> },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const isKbRoute = location.pathname.startsWith('/mvp-overview');
  const isResearchPaper = location.pathname.includes('research-paper');
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [kbAnchorEl, setKbAnchorEl] = useState(null);
  const user = authService.getStoredUser();
  const [currentTime, setCurrentTime] = useState('');
  // On research paper pages the AppBar is hidden by default to maximize editor/PDF space.
  // A small floating IconButton reveals it; an X inside the AppBar dismisses it again.
  // State resets on reload (intentional — no localStorage persistence).
  const [headerHidden, setHeaderHidden] = useState(true);

  // Update time every second - format directly in PKT
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      
      // Use formatToParts to get reliable timezone conversion
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Karachi',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      
      const parts = formatter.formatToParts(now);
      
      const year = parts.find(p => p.type === 'year')?.value || '';
      const month = parts.find(p => p.type === 'month')?.value || '';
      const day = parts.find(p => p.type === 'day')?.value || '';
      let hour = parts.find(p => p.type === 'hour')?.value || '0';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      const second = parts.find(p => p.type === 'second')?.value || '00';
      
      // Fix hour if it's 24 (should be 00)
      if (hour === '24') {
        hour = '00';
      } else if (hour && parseInt(hour) < 10) {
        hour = hour.padStart(2, '0');
      }
      
      const dateStr = `${day}/${month}/${year}`;
      const timeStr = `${hour}:${minute}:${second}`;
      
      setCurrentTime(`${dateStr}, ${timeStr}`);
    };
    
    // Update immediately
    updateTime();
    
    // Then update every second
    const timer = setInterval(updateTime, 1000);

    return () => clearInterval(timer);
  }, []);

  const navItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'Audits', path: '/firewalls', icon: <SendIcon /> },
    { text: 'Reports', path: '/history', icon: <DescriptionIcon /> },
    { text: 'SIEM', path: '/syslog', icon: <SecurityIcon /> },
    { text: 'SOAR', path: '/soar', icon: <BugReportIcon /> },
    { text: 'LLMs', path: '/llms', icon: <SmartToyIcon /> },
    { text: 'AIChat', path: '/ai-chat', icon: (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HeadsetMicIcon sx={{ fontSize: 16 }} />
      </Box>
    ) },
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setKbAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    handleMenuClose();
  };

  return (
    <Box sx={{
      ...(isResearchPaper
        ? { height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
        : { minHeight: '100vh', display: 'flex', flexDirection: 'column' }),
    }}>
      {/* Floating "Show navigation" affordance — visible only on research paper pages when AppBar is hidden */}
      {isResearchPaper && headerHidden && (
        <Tooltip title="Show navigation" placement="right">
          <IconButton
            aria-label="Show navigation"
            onClick={() => setHeaderHidden(false)}
            size="small"
            sx={{
              position: 'fixed',
              top: 8,
              left: 8,
              zIndex: 1100,
              backgroundColor: 'rgba(15, 23, 42, 0.7)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.9)' },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Collapse in={!isResearchPaper || !headerHidden} timeout={250} unmountOnExit>
      <AppBar position="static" sx={{ backgroundColor: '#0f172a', borderBottom: '1px solid rgba(148, 163, 184, 0.12)' }}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => window.location.href = '/'}
          >
            <Box
              component="img"
              src={Logo}
              alt="NSCAT Logo"
              sx={{
                width: 40,
                height: 40,
                marginRight: 2,
                borderRadius: '50%',
                objectFit: 'cover',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
                Network Security Configuration Audit Tool
              </Typography>
              <Typography variant="caption" component="div" sx={{ lineHeight: 1.2 }}>
                An AI based Network Configuration Auditing System for Multi-Vendor Firewalls
              </Typography>
            </Box>
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {navItems.map((item) => (
              <React.Fragment key={item.path}>
                <Button
                  component={Link}
                  to={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  sx={{
                    backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              </React.Fragment>
            ))}
            
            {/* User Menu - Only the icon, no username display */}
            {user && (
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                sx={{ ml: 2 }}
              >
                <AccountCircleIcon />
              </IconButton>
            )}
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {user?.role === 'admin' && (
                <MenuItem
                  onClick={(e) => setKbAnchorEl(e.currentTarget)}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 1 }} />
                    Knowledge Base
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18, ml: 1, color: 'text.secondary' }} />
                </MenuItem>
              )}
              <Menu
                anchorEl={kbAnchorEl}
                open={Boolean(kbAnchorEl)}
                onClose={() => setKbAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ '& .MuiPaper-root': { minWidth: 240 } }}
              >
                <MenuItem
                  component={Link}
                  to="/mvp-overview"
                  onClick={() => { setKbAnchorEl(null); handleMenuClose(); }}
                  sx={{ fontWeight: 600 }}
                >
                  <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                  All Documents
                </MenuItem>
                <Divider />
                {knowledgeBaseDocuments.filter((doc) => !doc.adminOnly || authService.isAdmin()).map((doc) => (
                  <MenuItem
                    key={doc.path}
                    component={Link}
                    to={doc.path}
                    onClick={() => { setKbAnchorEl(null); handleMenuClose(); }}
                    selected={location.pathname === doc.path}
                    sx={{ py: 1 }}
                  >
                    <Box sx={{ mr: 1.5, display: 'flex', color: 'text.secondary' }}>{doc.icon}</Box>
                    <Typography variant="body2">{doc.text}</Typography>
                  </MenuItem>
                ))}
              </Menu>
              {user?.role === 'admin' && (
                <MenuItem
                  component={Link}
                  to="/users"
                  onClick={handleMenuClose}
                >
                  <PeopleIcon sx={{ mr: 1 }} />
                  User Management
                </MenuItem>
              )}
              {user?.role === 'admin' && (
                <MenuItem
                  component={Link}
                  to="/database-backup"
                  onClick={handleMenuClose}
                >
                  <BackupIcon sx={{ mr: 1 }} />
                  Database Backup
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ mt: 0.5, borderTop: '1px solid', borderColor: 'divider', gap: 1, alignItems: 'flex-start' }}>
                <LogoutIcon sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="body2" fontWeight="bold" lineHeight={1.2}>
                    {user?.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {user?.role === 'user' ? 'Auditor' : user?.role}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Logout
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
            {/* Dismiss the AppBar back to hidden — only on research paper pages */}
            {isResearchPaper && (
              <Tooltip title="Hide navigation">
                <IconButton
                  aria-label="Hide navigation"
                  color="inherit"
                  onClick={() => setHeaderHidden(true)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      </Collapse>

      {/* System Time Display Bar - Below Banner (hidden on research paper pages) */}
      {!location.pathname.includes('research-paper') && (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#0f172a',
          border: 'none',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
          py: 0.75,
          px: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            letterSpacing: 0.5,
          }}
        >
          {currentTime || 'Loading...'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            fontWeight: 400,
            ml: 0.5,
          }}
        >
          PKT
        </Typography>
      </Paper>
      )}

      {isKbRoute ? (
        <Box component="main" sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {children}
        </Box>
      ) : (
        <Container component="main" sx={{ flexGrow: 1, maxWidth: '100%', py: 4, px: 1 }}>
          {children}
        </Container>
      )}
      {/* Footer - hidden on research paper pages */}
      {!location.pathname.includes('research-paper') && (
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: '#0f172a',
          borderTop: '1px solid rgba(148, 163, 184, 0.12)',
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <img
                src={Logo}
                alt="NSCAT Logo"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ fontWeight: 500 }}>
                Network Security Configuration Audit Tool (NSCAT)
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              © {new Date().getFullYear()} All rights reserved.
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              An AI based Multi-Vendor Auditing System for Network Security Devices
            </Typography>
          </Box>
        </Container>
      </Box>
      )}
    </Box>
  );
};

export default Layout; 
